/**
 * Les réactions d'un message — sous la bulle, emoji + compte ; la nôtre à la marque (c'est la
 * choisie). Cliquables, ce sont des `Button` ; sinon des `Badge`. Et `ReactionsRapides` : la
 * rangée d'emojis qu'on pose d'un clic (la barre d'outils d'une bulle, le menu d'un message).
 *
 *   <Reactions reactions={[{ cle: "🔥", emoji: "🔥", compte: 3, moi: true }]} onBasculer={(r) => …} />
 *   <ReactionsRapides emojis={["👍", "❤️", "🔥"]} actuelle="🔥" onChoisir={(e) => …} />
 */
import type { ReactNode } from "react";

import { Badge } from "../Badge";
import { Button } from "../Button";
import { cn } from "../utils";
import "./conversation.css";

export interface Reaction {
  /** Identifie la réaction (l'emoji lui-même, ou l'id d'un emoji personnalisé). */
  cle: string;
  /** Ce qu'on voit : le caractère, ou l'image d'un emoji personnalisé. */
  emoji: ReactNode;
  /** Combien l'ont posée. Absent : pas de chiffre (une réaction de bot, seule). */
  compte?: number;
  /** C'est la nôtre. */
  moi?: boolean;
  /** Dit au lecteur d'écran (« 🔥, 3 personnes »). Défaut : la clé et le compte. */
  libelle?: string;
}

export interface ReactionsProps {
  reactions: readonly Reaction[];
  /** Poser ou retirer la nôtre. Absent : des pastilles, pas des boutons. */
  onBasculer?: (reaction: Reaction) => void;
  ariaLabel?: string;
  className?: string;
}

/** Un emoji se lit à sa taille de texte, pas en petites capitales. */
const EMOJI = "text-[15px] leading-none normal-case tracking-normal";

export function Reactions({ reactions, onBasculer, ariaLabel = "Réactions", className }: ReactionsProps) {
  if (reactions.length === 0) return null;
  return (
    <div className={cn("cv-reactions", className)} role="group" aria-label={ariaLabel}>
      {reactions.map((r) => {
        const compte = r.compte !== undefined && r.compte > 0 ? r.compte : null;
        const dit = r.libelle ?? (compte !== null ? `${r.cle} ${compte}` : r.cle);
        const contenu = (
          <>
            <span className={EMOJI} aria-hidden>
              {r.emoji}
            </span>
            {compte !== null && <span aria-hidden>{compte}</span>}
          </>
        );
        return onBasculer ? (
          <Button
            key={r.cle}
            variant={r.moi ? "accent" : "secondary"}
            size="sm"
            className="h-7 gap-1.5 px-2.5"
            aria-pressed={r.moi ?? false}
            aria-label={dit}
            onClick={() => onBasculer(r)}
          >
            {contenu}
          </Button>
        ) : (
          <span key={r.cle} role="img" aria-label={dit} className="inline-flex">
            <Badge ton={r.moi ? "marque" : "gris"} size="md" className="gap-1.5">
              {contenu}
            </Badge>
          </span>
        );
      })}
    </div>
  );
}

/** Les emojis qu'on pose d'un clic, dans l'ordre des messageries. */
export const REACTIONS_RAPIDES = ["👍", "❤️", "🔥", "😂", "😮", "🙏"] as const;

export interface ReactionsRapidesProps {
  /** Défaut `REACTIONS_RAPIDES`. */
  emojis?: readonly string[];
  /** La nôtre, déjà posée : un second clic la retire (`onChoisir(null)`). */
  actuelle?: string | null;
  onChoisir: (emoji: string | null) => void;
  ariaLabel?: string;
  /** Sans le fond en verre : posée dans une barre qui en a déjà un. */
  nue?: boolean;
  className?: string;
}

export function ReactionsRapides({
  emojis = REACTIONS_RAPIDES,
  actuelle = null,
  onChoisir,
  ariaLabel = "Réagir",
  nue = false,
  className,
}: ReactionsRapidesProps) {
  return (
    <span className={cn(!nue && "cv-rapides", "inline-flex items-center gap-0.5", className)} role="group" aria-label={ariaLabel}>
      {emojis.map((e) => (
        <Button
          key={e}
          variant={actuelle === e ? "accent" : "ghost"}
          size="lg"
          iconOnly
          className="h-8 w-8 text-[18px] leading-none sm:h-9 sm:w-9 sm:text-[20px]"
          aria-pressed={actuelle === e}
          aria-label={e}
          onClick={(ev) => {
            ev.stopPropagation();
            onChoisir(actuelle === e ? null : e);
          }}
        >
          {e}
        </Button>
      ))}
    </span>
  );
}
